import React, { useContext, useState } from 'react';
import _ from 'lodash';
import { getImageThumbnailMedium } from '../../../../lib/imageUtilsESM';
import { scaledWidthPx, computeSharedScale, boxAnchorMm } from '../../../../lib/miniatureUtilsESM';
import { miniatureImageUrl } from './miniatureUiUtils';
import useCachedBatchedGet from '../common/react-hooks/useGetCachedDocIdBatched';
import useAsyncEffect from '../common/react-hooks/useAsyncEffect';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';

const MINIATURE_SERVICE = '/services/data/model-miniatures';
// Batched + cached single-doc fetcher shared by every ScaledMiniature on the page.
const useMiniatureDoc = useCachedBatchedGet(MINIATURE_SERVICE);

/**
 * React port of the `scaledMiniature` pug mixin (views/core-design/miniature-mixins.pug).
 *
 * Renders a model_miniatures image at a shared physical scale (`pxPerMm`); optionally cropped to its
 * bounding box and/or with the box overlaid. Takes a RAW miniature doc — the shape returned by
 * /services/data/model-miniatures: `{ image:{name,width,height}, overlayBox, dimensions }`. The image
 * natural size comes from `image.width/height` (no client measuring). Styles are inline so it's
 * self-contained.
 *
 * @param {{ miniature: object, pxPerMm: number, showBox?: boolean, crop?: boolean, margin?: number }} props
 *   crop   → footprint is the bounding box + `margin` px; the full image bleeds outside (shadow shows)
 */
export default function ScaledMiniature({ miniature, miniatureId, pxPerMm, showBox = false, crop = false, margin = 0 }) {
  // When a full doc isn't passed, resolve it from its id (batched across the page + cached).
  const fetched = useMiniatureDoc(miniature ? null : (miniatureId || null));
  const m = miniature || (fetched && typeof fetched === 'object' ? fetched : null);

  const url = miniatureImageUrl(m && m.image);
  if (!url) return null;

  const box = (m && m.overlayBox) || null;
  const boxWidthMm = boxAnchorMm(m && m.dimensions);
  const natW = (m.image && m.image.width) || 0;
  const natH = (m.image && m.image.height) || 0;

  const width = scaledWidthPx(box, boxWidthMm, pxPerMm);
  const imgH = natW > 0 ? (width * natH) / natW : 0;

  const cropping = crop && !!box && imgH > 0;
  const cropW = box ? box.w * width + 2 * margin : width;
  const cropH = box ? box.h * imgH + 2 * margin : imgH;
  const offX = box ? margin - box.x * width : 0;
  const offY = box ? margin - box.y * imgH : 0;

  // clip: rounded card, hidden overflow. bleed: no clipping so the full image spills outside the box.
  const outerStyle = {
    position: 'relative',
    lineHeight: 0,
    ...(cropping
      ? { overflow: 'visible' }
      : { background: '#fafafa', borderRadius: '6px', overflow: 'hidden' }),
    ...(cropping ? { width: cropW, height: cropH } : { width }),
  };
  const innerStyle = cropping
    ? { position: 'absolute', left: offX, top: offY, width }
    : { position: 'relative', width: '100%' };
  const boxStyle = box
    ? {
      position: 'absolute',
      boxSizing: 'border-box',
      pointerEvents: 'none',
      border: '1px dashed rgba(0,0,255,0.3)',
      left: `${box.x * 100}%`,
      top: `${box.y * 100}%`,
      width: `${box.w * 100}%`,
      height: `${box.h * 100}%`,
    }
    : null;

  return <div className={'ScaledMiniature'} style={outerStyle}>
    <div className={'ScaledMiniature__inner'} style={innerStyle}>
      <img src={getImageThumbnailMedium(url)} alt={''} style={{ width: '100%', display: 'block' }} />
      {showBox && box ? <div className={'ScaledMiniature__bbox'} style={boxStyle} /> : null}
    </div>
  </div>;
}

/**
 * Shared `pxPerMm` so the largest bounding box across the given RAW miniature docs fits the bound(s).
 * Adapts the raw doc shape ({ image, overlayBox, dimensions }) to lib/miniatureUtils.computeSharedScale.
 * @param {Array<object>} miniatureDocs
 * @param {{maxWidth?:number, maxHeight?:number}} [bounds]
 * @returns {number} pxPerMm (0 when nothing constrains it / no usable data)
 */
export function computeMiniaturesScale(miniatureDocs, bounds) {
  const items = (miniatureDocs || []).filter(Boolean).map((m) => ({
    overlayBox: m.overlayBox,
    dimensions: m.dimensions,
    imgW: m.image && m.image.width,
    imgH: m.image && m.image.height,
  }));
  return computeSharedScale(items, bounds || {});
}

// Cache the docs fetched for a shared scale, keyed by the (sorted) set of ids — so many cells asking
// for the same scale only fetch once, and the result survives re-renders.
const scaleDocsCache = {};

/**
 * Hook: shared `pxPerMm` so the largest bounding box across `miniatureIds` fits `bounds`. Fetches the
 * referenced miniatures once (cached by id-set) and recomputes as they arrive. Use it where the
 * caller only has ids (e.g. a list of raw gens) and wants ScaledMiniature children to self-fetch.
 * @param {string[]} miniatureIds
 * @param {{maxWidth?:number, maxHeight?:number}} [bounds]
 * @returns {number} pxPerMm (0 until docs load / when empty)
 */
export function useMiniaturesScale(miniatureIds, bounds) {
  const { page } = useContext(LegoAdminPageContext);
  const ids = _.uniq(_.compact((miniatureIds || []).map(String)));
  const sig = ids.slice().sort().join(',');
  const [docs, setDocs] = useState(() => scaleDocsCache[sig] || null);

  useAsyncEffect(async (isMounted) => {
    if (scaleDocsCache[sig]) {
      if (isMounted() && docs !== scaleDocsCache[sig]) setDocs(scaleDocsCache[sig]);
      return;
    }
    if (!ids.length) return;
    try {
      const res = await page.service(MINIATURE_SERVICE).find({ query: { _id: { $in: ids }, $limit: ids.length } });
      scaleDocsCache[sig] = res;
      if (isMounted()) setDocs(res);
    } catch (err) {
      console.error('Failed loading miniatures for shared scale', err);
    }
  }, [sig, page]);

  return computeMiniaturesScale(docs || [], bounds);
}
